// =============================================================================
// Authentication Hook & Store
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import { authService } from '../services/auth.service';
import { clearTotpSession } from '../services/accountant.service';
import { clearPermissionsCache } from './usePermissions';

// =============================================================================
// Types
// =============================================================================

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean; // Track if initial auth check is complete
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearAuth: () => void; // Force clear auth state (for recovery)
}

// =============================================================================
// Prevent infinite loops
// =============================================================================

let isCheckingAuth = false;
let lastCheckTime = 0;
let failedCheckCount = 0;
const MIN_CHECK_INTERVAL = 5000; // Minimum 5 seconds between auth checks
const AUTH_CHECK_TIMEOUT = 10000; // Max 10 seconds for auth check before giving up
const MAX_FAILED_CHECKS = 3; // After 3 failed checks, clear auth completely

// =============================================================================
// Auth Store
// =============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      isInitialized: false,
      error: null,

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setAccessToken: (accessToken) => set({ accessToken }),

      setRefreshToken: (refreshToken) => set({ refreshToken }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      login: (user, accessToken, refreshToken) => {
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch {
          // Ignore logout errors
        } finally {
          clearTotpSession();
          clearPermissionsCache();
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
        }
      },

      // Force clear auth state - useful for recovery from bad states
      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      },

      checkAuth: async () => {
        // Prevent concurrent checks
        if (isCheckingAuth) {
          console.log('[Auth] Check already in progress, skipping');
          return;
        }

        // Prevent rapid-fire checks (rate limiting)
        const now = Date.now();
        if (now - lastCheckTime < MIN_CHECK_INTERVAL && get().isInitialized) {
          console.log('[Auth] Check too soon, skipping');
          return;
        }

        // If too many failed checks, clear auth and stop trying
        if (failedCheckCount >= MAX_FAILED_CHECKS) {
          console.log('[Auth] Too many failed checks, clearing auth');
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
          // Reset counter for next session
          failedCheckCount = 0;
          return;
        }

        isCheckingAuth = true;
        lastCheckTime = now;

        const { accessToken } = get();

        // No token - not authenticated
        if (!accessToken) {
          set({
            isLoading: false,
            isAuthenticated: false,
            isInitialized: true
          });
          isCheckingAuth = false;
          return;
        }

        // Create a timeout promise to prevent hanging
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), AUTH_CHECK_TIMEOUT);
        });

        try {
          // Race between actual auth check and timeout
          const user = await Promise.race([
            authService.getCurrentUser(),
            timeoutPromise
          ]);

          if (user) {
            // Success - reset failed counter
            failedCheckCount = 0;
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              isInitialized: true,
            });
          } else {
            // Try to refresh token once (with timeout)
            // Refresh token is now primarily cookie-based (HttpOnly).
            // Pass store token as fallback for backward compatibility.
            const { refreshToken } = get();
            try {
              const refreshResult = await Promise.race([
                authService.refreshToken(refreshToken || undefined),
                timeoutPromise.then(() => ({ accessToken: undefined }))
              ]);

              if (refreshResult.accessToken) {
                set({ accessToken: refreshResult.accessToken });
                const refreshedUser = await Promise.race([
                  authService.getCurrentUser(),
                  timeoutPromise
                ]);
                if (refreshedUser) {
                  // Success - reset failed counter
                  failedCheckCount = 0;
                }
                set({
                  user: refreshedUser,
                  isAuthenticated: !!refreshedUser,
                  isLoading: false,
                  isInitialized: true,
                });
              } else {
                // Refresh failed or timed out - clear auth
                failedCheckCount++;
                set({
                  user: null,
                  accessToken: null,
                  isAuthenticated: false,
                  isLoading: false,
                  isInitialized: true,
                });
              }
            } catch {
              // Refresh threw error - clear auth
              failedCheckCount++;
              set({
                user: null,
                accessToken: null,
                isAuthenticated: false,
                isLoading: false,
                isInitialized: true,
              });
            }
          }
        } catch (error) {
          console.error('[Auth] Check failed:', error);
          // On error, clear auth state to prevent loops
          failedCheckCount++;
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
          });
        } finally {
          isCheckingAuth = false;
        }
      },
    }),
    {
      name: 'auth-storage',
      // Persist both tokens. RefreshToken is also set as HttpOnly cookie
      // for primary security, but localStorage fallback ensures reliability
      // when browsers block cross-origin cookies.
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      version: 2,
      migrate: (persistedState: unknown) => {
        return persistedState as Record<string, unknown>;
      },
      // Handle storage errors gracefully
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('[Auth] Failed to load auth state:', error);
          // Clear corrupted storage
          localStorage.removeItem('auth-storage');
        }
        // Mark as initialized after rehydration
        if (state) {
          state.isInitialized = false; // Will be set to true after checkAuth
        }
      },
    }
  )
);

// =============================================================================
// Custom Hook
// =============================================================================

export function useAuth() {
  const {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,
    login,
    logout,
    checkAuth,
    setError,
    clearAuth,
  } = useAuthStore();

  return {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,
    login,
    logout,
    checkAuth,
    setError,
    clearAuth,
    isAdmin: user?.role === 'ADMIN',
    isTeam: user?.role === 'TEAM',
    isInvestor: user?.role === 'INVESTOR',
  };
}

export default useAuth;
